/**
 * One quiz result, question by question.
 *
 * Open to the student it belongs to and to staff who can see that student;
 * firestore.rules decides, and a denied read shows as "not available" rather
 * than an error page. Server-graded results carry each question as it was
 * asked. Older ones stored only the chosen option per question id, so the
 * questions are looked up from chapter_mcqs and graded with the same resolver
 * the players use.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, Check, X } from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getResult } from '../../services/performanceService';
import { extractMcqOptions, resolveCorrectAnswer } from '../../lib/mcq/answerIndex';
import {
  formatDuration,
  formatPercent,
  lessonTitle,
  scoreTone,
  type ResponseRecord,
  type Result,
} from '../../lib/analytics/metrics';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { MetricCard } from '../../Components/analytics/MetricCard';
import { AnalyticsEmptyState, AnalyticsNote, AnalyticsPanel, TableSkeleton } from '../../Components/analytics/AnalyticsPanel';
import { Badge } from '../../Components/ui/badge';

const letter = (i: number) => String.fromCharCode(65 + i);

async function responsesFromLegacyAnswers(answers: Record<string, number>): Promise<ResponseRecord[]> {
  const ids = Object.keys(answers);
  const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'chapter_mcqs', id)).catch(() => null)));
  return ids.map((id, i) => {
    const data = snaps[i]?.exists() ? (snaps[i]!.data() as Record<string, unknown>) : null;
    const options = extractMcqOptions(data);
    const correct = resolveCorrectAnswer(data, options).index;
    return {
      question_id: id,
      question_text: data ? String(data.question ?? data.question_text ?? '') : '',
      options,
      student_answer_index: answers[id],
      correct_answer_index: correct,
      is_correct: correct >= 0 && answers[id] === correct,
    };
  });
}

const ResultDetail = () => {
  const { scoreId } = useParams<{ scoreId: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [result, setResult] = useState<Result | null>(null);
  const [responses, setResponses] = useState<ResponseRecord[]>([]);
  const [reconstructed, setReconstructed] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    if (!scoreId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getResult(scoreId);
        if (cancelled) return;
        if (!r) return setState('missing');
        setResult(r);
        if (r.responses.length > 0) {
          setResponses(r.responses);
        } else if (Object.keys(r.answers).length > 0) {
          const rebuilt = await responsesFromLegacyAnswers(r.answers);
          if (cancelled) return;
          setResponses(rebuilt);
          setReconstructed(true);
        }
        setState('ready');
      } catch {
        if (!cancelled) setState('missing');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scoreId]);

  const isOwn = !!result && profile?.uid === result.studentId;

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-4xl space-y-5 px-4 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        {state === 'loading' && <TableSkeleton rows={6} />}

        {state === 'missing' && (
          <AnalyticsPanel title="Result not available">
            <AnalyticsEmptyState
              title="This result doesn’t exist or you don’t have access to it"
              description="Results are visible to the student and to their teachers and school."
            />
          </AnalyticsPanel>
        )}

        {state === 'ready' && result && (
          <>
            <header>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {isOwn ? 'Your result' : result.studentName ?? 'Student result'}
              </p>
              <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
                {lessonTitle(result)}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {[
                  result.subject,
                  result.classLabel,
                  result.completedAt ? new Date(result.completedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null,
                  `Attempt ${result.attempt}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {!isOwn && profile?.role !== 'student' && result.studentId && (
                <Link to={`/analytics/students/${result.studentId}`} className="mt-2 inline-block text-xs font-medium text-primary hover:underline">
                  View this student’s performance →
                </Link>
              )}
            </header>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <MetricCard label="Score" value={`${result.marksObtained} / ${result.maximumMarks}`} detail="Marks" />
              <MetricCard
                label="Percentage"
                value={<span className={scoreTone(result.attempted > 0 ? result.percentage : null)}>{formatPercent(result.percentage)}</span>}
                definition="Marks obtained ÷ maximum marks."
              />
              <MetricCard label="Accuracy" value={formatPercent(result.accuracy)} detail="Correct of attempted" />
              <MetricCard label="Completion" value={formatPercent(result.completion)} detail={`${result.attempted} of ${result.totalQuestions} answered`} />
              <MetricCard label="Time" value={formatDuration(result.timeSeconds)} />
            </div>

            <AnalyticsPanel title="Questions" description={`${result.correct} correct · ${result.incorrect} incorrect · ${result.unanswered} not answered`}>
              {responses.length === 0 ? (
                <AnalyticsEmptyState compact title="No answers recorded" description="This result has no per-question answers." />
              ) : (
                <ol className="space-y-3">
                  {responses.map((r, i) => {
                    const answered = r.student_answer_index !== null;
                    return (
                      <li key={r.question_id} className="rounded-lg border border-border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-foreground">
                            <span className="mr-1.5 font-semibold text-muted-foreground">Q{i + 1}</span>
                            {r.question_text || 'Question text unavailable'}
                          </p>
                          {!answered ? (
                            <Badge variant="outline" className="shrink-0 text-[10px]">Not answered</Badge>
                          ) : r.is_correct ? (
                            <Badge variant="outline" className="shrink-0 gap-1 border-primary/50 text-[10px] text-primary">
                              <Check className="h-3 w-3" aria-hidden /> Correct
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="shrink-0 gap-1 border-destructive/50 text-[10px] text-destructive">
                              <X className="h-3 w-3" aria-hidden /> Incorrect
                            </Badge>
                          )}
                        </div>
                        {r.options && r.options.length > 0 && (
                          <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                            {r.options.map((opt, oi) => {
                              const isCorrect = oi === r.correct_answer_index;
                              const isChosen = oi === r.student_answer_index;
                              return (
                                <li
                                  key={oi}
                                  className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                                    isCorrect
                                      ? 'border-primary/50 bg-primary/5 text-foreground'
                                      : isChosen
                                        ? 'border-destructive/50 bg-destructive/5 text-foreground'
                                        : 'border-border text-muted-foreground'
                                  }`}
                                >
                                  <span className="font-semibold">{letter(oi)}.</span>
                                  <span className="flex-1">{opt}</span>
                                  {isChosen && <span className="shrink-0 text-[10px] uppercase tracking-wide">{isOwn ? 'Your answer' : 'Chosen'}</span>}
                                  {isCorrect && !isChosen && <span className="shrink-0 text-[10px] uppercase tracking-wide text-primary">Answer</span>}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
              {reconstructed && (
                <AnalyticsNote>
                  This result was recorded before questions were stored with it. Questions are shown as they are now in the lesson, and
                  questions left unanswered are not listed.
                </AnalyticsNote>
              )}
            </AnalyticsPanel>
          </>
        )}
      </div>
    </div>
  );
};

export default ResultDetail;
